// API Services for CLMS SaaS Frontend
import axios from "axios";

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL || "";
axios.defaults.timeout = 30000;

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant ID header if available
    const tenantId = localStorage.getItem("tenantId");
    if (tenantId) {
      config.headers["X-Tenant-ID"] = tenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("tenantId");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => axios.post("/api/auth/login", credentials),
  register: (userData) => axios.post("/api/auth/register", userData),
  forgotPassword: (email) => axios.post("/api/auth/forgot-password", { email }),
  resetPassword: (token, password) =>
    axios.post("/api/auth/reset-password", { token, password }),
  getProfile: () => axios.get("/api/auth/profile"),
  updateProfile: (data) => axios.put("/api/auth/profile", data),
  changePassword: (data) => axios.put("/api/auth/change-password", data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (params) =>
    axios.get("/api/dashboard/stats", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getChartData: (period) => axios.get(`/api/dashboard/charts?period=${period}`),
  getRecentActivity: (params) =>
    axios.get("/api/dashboard/recent", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getAttendanceOverview: (params) =>
    axios.get("/api/dashboard/attendance", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getComplianceOverview: () =>
    axios.get("/api/dashboard/compliance", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getNotifications: (params) =>
    axios.get("/api/dashboard/notifications", { params }),
};

// Workers API (Legacy)
export const workersAPI = {
  getAll: (params) => axios.get("/api/workers", { params }),
  getById: (id) => axios.get(`/api/workers/${id}`),
  create: (data) => axios.post("/api/workers", data),
  update: (id, data) => axios.put(`/api/workers/${id}`, data),
  delete: (id) => axios.delete(`/api/workers/${id}`),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post("/api/workers/bulk-import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  export: (params) =>
    axios.get("/api/workers/export", { params, responseType: "blob" }),
  uploadDocument: (workerId, file, documentType) => {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("type", documentType);
    return axios.post(`/api/workers/${workerId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Worker Details API (v2.3)
export const workerDetailsAPI = {
  getAll: (params) => axios.get("/api/worker-details", { params }),
  getById: (id) => axios.get(`/api/worker-details/${id}`),
  create: (data) => axios.post("/api/worker-details", data),
  update: (id, data) => axios.put(`/api/worker-details/${id}`, data),
  delete: (id) => axios.delete(`/api/worker-details/${id}`),

  // Advanced filtering
  filter: (filters) => axios.post("/api/worker-details/filter", filters),
  search: (params) => axios.get("/api/worker-details/search", { params }),

  // Document validation
  validateDocument: (id, documentData) =>
    axios.patch(`/api/worker-details/${id}/validate-document`, documentData),

  // Statistics
  getStatistics: () => axios.get("/api/worker-details/statistics"),

  // File uploads
  uploadDocument: (workerId, file, documentType) => {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("type", documentType);
    return axios.post(`/api/worker-details/${workerId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Export
  export: (params) =>
    axios.get("/api/worker-details/export", { params, responseType: "blob" }),
};

// Contractors API (Legacy)
export const contractorsAPI = {
  getAll: (params) => axios.get("/api/contractors", { params }),
  getById: (id) => axios.get(`/api/contractors/${id}`),
  create: (data) => axios.post("/api/contractors", data),
  update: (id, data) => axios.put(`/api/contractors/${id}`, data),
  delete: (id) => axios.delete(`/api/contractors/${id}`),
  getWorkers: (id, params) =>
    axios.get(`/api/contractors/${id}/workers`, { params }),
  uploadDocument: (contractorId, file, documentType) => {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("type", documentType);
    return axios.post(`/api/contractors/${contractorId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Contractor Details API (v2.3)
export const contractorDetailsAPI = {
  getAll: (params) => axios.get("/api/contractor-details", { params }),
  getById: (id) => axios.get(`/api/contractor-details/${id}`),
  create: (data) => axios.post("/api/contractor-details", data),
  update: (id, data) => axios.put(`/api/contractor-details/${id}`, data),
  delete: (id) => axios.delete(`/api/contractor-details/${id}`),

  // Status management
  updateStatus: (id, statusData) =>
    axios.patch(`/api/contractor-details/${id}/status`, statusData),

  // User management
  approveUser: (userId, approvalData) =>
    axios.patch(
      `/api/contractor-details/users/${userId}/approve`,
      approvalData
    ),

  // File uploads
  uploadDocument: (contractorId, file, documentType) => {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("type", documentType);
    return axios.post(
      `/api/contractor-details/${contractorId}/documents`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  },

  // Profile management
  getContractorProfile: () => axios.get("/api/contractor-details/profile"),
  updateContractorProfile: (data) =>
    axios.put("/api/contractor-details/profile", data),
  getProfileStatus: () => axios.get("/api/contractor-details/profile/status"),
  completeProfile: () => axios.post("/api/contractor-details/profile/complete"),

  // Export
  export: (params) =>
    axios.get("/api/contractor-details/export", {
      params,
      responseType: "blob",
    }),
};

// Attendance API
export const attendanceAPI = {
  getAll: (params) => axios.get("/api/attendance", { params }),
  getByDate: (date) => axios.get(`/api/attendance/date/${date}`),
  getByWorker: (workerId, params) =>
    axios.get(`/api/attendance/worker/${workerId}`, { params }),
  markAttendance: (data) => axios.post("/api/attendance/mark", data),
  bulkMarkAttendance: (data) => axios.post("/api/attendance/bulk-mark", data),
  updateAttendance: (id, data) => axios.put(`/api/attendance/${id}`, data),
  getReport: (params) => axios.get("/api/attendance/report", { params }),
  export: (params) =>
    axios.get("/api/attendance/export", { params, responseType: "blob" }),
};

// Compliance API
export const complianceAPI = {
  getForms: (params) => axios.get("/api/compliance/forms", { params }),
  getFormById: (id) => axios.get(`/api/compliance/forms/${id}`),
  createForm: (data) => axios.post("/api/compliance/forms", data),
  updateForm: (id, data) => axios.put(`/api/compliance/forms/${id}`, data),
  submitForm: (id) => axios.post(`/api/compliance/forms/${id}/submit`),
  downloadForm: (id) =>
    axios.get(`/api/compliance/forms/${id}/download`, { responseType: "blob" }),
  getFormTemplates: () => axios.get("/api/compliance/templates"),
  generateForm: (type, data) =>
    axios.post(`/api/compliance/generate/${type}`, data),
};

// Reports API
export const reportsAPI = {
  getAvailableReports: () => axios.get("/api/reports/available"),
  generateReport: (type, params) =>
    axios.post(`/api/reports/generate/${type}`, params),
  getReport: (id) => axios.get(`/api/reports/${id}`),
  downloadReport: (id, format) =>
    axios.get(`/api/reports/${id}/download/${format}`, {
      responseType: "blob",
    }),
  scheduleReport: (data) => axios.post("/api/reports/schedule", data),
  getScheduledReports: () => axios.get("/api/reports/scheduled"),
};

// Devices API
export const devicesAPI = {
  getAll: (params) => axios.get("/api/devices", { params }),
  getById: (id) => axios.get(`/api/devices/${id}`),
  create: (data) => axios.post("/api/devices", data),
  update: (id, data) => axios.put(`/api/devices/${id}`, data),
  delete: (id) => axios.delete(`/api/devices/${id}`),
  sync: (id) => axios.post(`/api/devices/${id}/sync`),
  testConnection: (id) => axios.post(`/api/devices/${id}/test`),
  getAttendanceData: (id, params) =>
    axios.get(`/api/devices/${id}/attendance`, { params }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => axios.get("/api/notifications", { params }),
  getNotifications: (params) =>
    axios.get("/api/notifications", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  markAsRead: (id) => axios.put(`/api/notifications/${id}/read`),
  markAllAsRead: () => axios.put("/api/notifications/mark-all-read"),
  delete: (id) => axios.delete(`/api/notifications/${id}`),
  deleteNotification: (id) =>
    axios.delete(`/api/notifications/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  create: (data) => axios.post("/api/notifications", data),
  createNotification: (data) =>
    axios.post("/api/notifications", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }),
  getUnreadCount: () => axios.get("/api/notifications/unread-count"),
  getCounts: () =>
    axios.get("/api/notifications/counts", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
};

// Settings API
export const settingsAPI = {
  get: () => axios.get("/api/settings"),
  getSettings: () =>
    axios.get("/api/settings", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  update: (data) => axios.put("/api/settings", data),
  updateSettings: (data) =>
    axios.put("/api/settings", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }),
  updatePassword: (data) =>
    axios.put("/api/settings/password", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }),
  getNotificationPreferences: () =>
    axios.get("/api/settings/notifications", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  updateNotificationPreferences: (data) =>
    axios.put("/api/settings/notifications", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }),
  getSystemInfo: () =>
    axios.get("/api/settings/system", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  testSMTP: (config) => axios.post("/api/settings/test-smtp", config),
  testCloudStorage: (provider, config) =>
    axios.post(`/api/settings/test-storage/${provider}`, config),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append("logo", file);
    return axios.post("/api/settings/upload-logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Super Admin API
export const superAdminAPI = {
  getDashboard: () =>
    axios.get("/api/superadmin/dashboard", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),

  // Tenant Management APIs
  getTenants: (params) =>
    axios.get("/api/superadmin/tenants", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getTenant: (id) =>
    axios.get(`/api/superadmin/tenants/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  createTenant: (data) =>
    axios.post("/api/superadmin/tenants", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }),
  updateTenant: (id, data) =>
    axios.put(`/api/superadmin/tenants/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }),
  deleteTenant: (id, hardDelete = false) =>
    axios.delete(`/api/superadmin/tenants/${id}?hardDelete=${hardDelete}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  suspendTenant: (id, reason) =>
    axios.put(
      `/api/superadmin/tenants/${id}/suspend`,
      { reason },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    ),
  reactivateTenant: (id) =>
    axios.put(
      `/api/superadmin/tenants/${id}/reactivate`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    ),
  getTenantStats: (id) =>
    axios.get(`/api/superadmin/tenants/${id}/stats`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getAnalytics: (params) =>
    axios.get("/api/superadmin/analytics", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getSystemHealth: () =>
    axios.get("/api/superadmin/health", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),

  // Contractor Management
  getContractors: (params) =>
    axios.get("/api/superadmin/contractors", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getContractor: (contractorId) =>
    axios.get(`/api/superadmin/contractors/${contractorId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  updateContractorStatus: (contractorId, status, reason) =>
    axios.patch(
      `/api/superadmin/contractors/${contractorId}/status`,
      { status, reason },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    ),
  approveContractorUser: (userId, approved, permissions) =>
    axios.patch(
      `/api/superadmin/contractors/users/${userId}/approve`,
      { approved, permissions },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    ),
  deleteContractor: (contractorId, hardDelete = false) =>
    axios.delete(
      `/api/superadmin/contractors/${contractorId}?hardDelete=${hardDelete}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    ),
};

// Usage API
export const usageAPI = {
  getUsageStats: (params) =>
    axios.get("/api/usage", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getDetailedStats: (params) =>
    axios.get("/api/usage/stats", {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getSystemHealth: () =>
    axios.get("/api/usage/health", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
};

// File Upload API
export const fileAPI = {
  upload: (files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    return axios.post("/api/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        if (process.env.NODE_ENV !== "production") {
          console.log(`Upload Progress: ${percentCompleted}%`);
        }
      },
    });
  },
  delete: (filename) => axios.delete(`/api/upload/${filename}`),
};

// Shift Management API - NEW SHIFT MANAGEMENT SYSTEM
export const shiftManagementAPI = {
  // Shifts API
  getAll: (params) => axios.get("/api/shift-management/shifts", { params }),
  getById: (id) => axios.get(`/api/shift-management/shifts/${id}`),
  create: (data) => axios.post("/api/shift-management/shifts", data),
  update: (id, data) => axios.put(`/api/shift-management/shifts/${id}`, data),
  delete: (id) => axios.delete(`/api/shift-management/shifts/${id}`),
  toggleStatus: (id) =>
    axios.patch(`/api/shift-management/shifts/${id}/toggle-status`),
  getDropdown: () => axios.get("/api/shift-management/shifts/dropdown"),

  // Shift Categories API
  getAllCategories: (params) =>
    axios.get("/api/shift-management/shift-categories", { params }),
  getCategoryById: (id) =>
    axios.get(`/api/shift-management/shift-categories/${id}`),
  createCategory: (data) =>
    axios.post("/api/shift-management/shift-categories", data),
  updateCategory: (id, data) =>
    axios.put(`/api/shift-management/shift-categories/${id}`, data),
  deleteCategory: (id) =>
    axios.delete(`/api/shift-management/shift-categories/${id}`),
  toggleCategoryStatus: (id) =>
    axios.patch(`/api/shift-management/shift-categories/${id}/toggle-status`),
  getCategoriesDropdown: () =>
    axios.get("/api/shift-management/shift-categories/dropdown"),
};

// Legacy Shift Policies API (keeping for backwards compatibility)
export const shiftsAPI = {
  getPolicies: (params) => axios.get("/api/shifts/policies", { params }),
  createPolicy: (data) => axios.post("/api/shifts/policies", data),
  updatePolicy: (id, data) => axios.put(`/api/shifts/policies/${id}`, data),
  deletePolicy: (id) => axios.delete(`/api/shifts/policies/${id}`),
  addShift: (policyId, data) =>
    axios.post(`/api/shifts/policies/${policyId}/shifts`, data),
  updateShift: (policyId, shiftId, data) =>
    axios.put(`/api/shifts/policies/${policyId}/shifts/${shiftId}`, data),
  deleteShift: (policyId, shiftId) =>
    axios.delete(`/api/shifts/policies/${policyId}/shifts/${shiftId}`),
};

// Attendance Policies API
export const attendancePoliciesAPI = {
  getAll: (params) => axios.get("/api/attendance-policies", { params }),
  getById: (id) => axios.get(`/api/attendance-policies/${id}`),
  create: (data) => axios.post("/api/attendance-policies", data),
  update: (id, data) => axios.put(`/api/attendance-policies/${id}`, data),
  delete: (id) => axios.delete(`/api/attendance-policies/${id}`),
};

// CLRA Reports API
export const clraReportsAPI = {
  getTypes: () => axios.get("/api/clra-reports/types"),
  generate: (reportType, data) =>
    axios.post(`/api/clra-reports/generate/${reportType}`, data),
  getAll: (params) => axios.get("/api/clra-reports", { params }),
  getById: (id) => axios.get(`/api/clra-reports/${id}`),
  downloadExcel: (id) =>
    axios.get(`/api/clra-reports/${id}/excel`, {
      responseType: "blob",
    }),
  downloadPDF: (id) =>
    axios.get(`/api/clra-reports/${id}/pdf`, {
      responseType: "blob",
    }),
  delete: (id) => axios.delete(`/api/clra-reports/${id}`),
};

// Statutory Reports API
export const statutoryReportsAPI = {
  getTypes: () => axios.get("/api/statutory-reports/types"),
  generate: (reportType, data) =>
    axios.post(`/api/statutory-reports/generate/${reportType}`, data),
  getAll: (params) => axios.get("/api/statutory-reports", { params }),
  getById: (id) => axios.get(`/api/statutory-reports/${id}`),
  downloadExcel: (id) =>
    axios.get(`/api/statutory-reports/${id}/excel`, {
      responseType: "blob",
    }),
  downloadPDF: (id) =>
    axios.get(`/api/statutory-reports/${id}/pdf`, {
      responseType: "blob",
    }),
  delete: (id) => axios.delete(`/api/statutory-reports/${id}`),
  getStateRules: (state) =>
    axios.get(`/api/statutory-reports/state-rules/${state}`),
};

// Analytics Reports API
export const analyticsReportsAPI = {
  getTypes: () => axios.get("/api/analytics-reports/types"),
  generate: (reportType, data) =>
    axios.post(`/api/analytics-reports/generate/${reportType}`, data),
  getAll: (params) => axios.get("/api/analytics-reports", { params }),
  getById: (id) => axios.get(`/api/analytics-reports/${id}`),
  downloadExcel: (id) =>
    axios.get(`/api/analytics-reports/${id}/excel`, {
      responseType: "blob",
    }),
  downloadPDF: (id) =>
    axios.get(`/api/analytics-reports/${id}/pdf`, {
      responseType: "blob",
    }),
  getDashboardOverview: () =>
    axios.get("/api/analytics-reports/dashboard/overview"),
  getKPISummary: () => axios.get("/api/analytics-reports/kpi/summary"),
  getTrends: (metric, period) =>
    axios.get(`/api/analytics-reports/trends/${metric}`, {
      params: { period },
    }),
  delete: (id) => axios.delete(`/api/analytics-reports/${id}`),
};

// Work Order Details API (v2.3)
export const workOrderDetailsAPI = {
  getAll: (params) => axios.get("/api/work-order-details", { params }),
  getById: (id) => axios.get(`/api/work-order-details/${id}`),
  create: (data) => axios.post("/api/work-order-details", data),
  update: (id, data) => axios.put(`/api/work-order-details/${id}`, data),
  delete: (id) => axios.delete(`/api/work-order-details/${id}`),

  // Status management
  updateStatus: (id, statusData) =>
    axios.patch(`/api/work-order-details/${id}/status`, statusData),

  // Worker assignment
  assignWorkers: (id, workersData) =>
    axios.post(`/api/work-order-details/${id}/assign-workers`, workersData),

  // Statistics
  getStatistics: () =>
    axios.get("/api/work-order-details/statistics/dashboard"),

  // Search and filtering
  search: (filters) => axios.post("/api/work-order-details/search", filters),
  getFilterOptions: () => axios.get("/api/work-order-details/filter-options"),

  // Export
  export: (params) =>
    axios.get("/api/work-order-details/export", {
      params,
      responseType: "blob",
    }),

  // Contractor assignment
  assignContractor: (id, contractorData) =>
    axios.post(
      `/api/work-order-details/${id}/assign-contractor`,
      contractorData
    ),
};

// Contractor Work Order API
export const contractorWorkOrderAPI = {
  // Get assigned work orders for contractor
  getAssignedWorkOrders: (params) =>
    axios.get("/api/contractor/work-orders", { params }),

  // Get work order details for contractor
  getWorkOrderDetails: (id) => axios.get(`/api/contractor/work-orders/${id}`),

  // Update work order progress
  updateProgress: (id, progressData) =>
    axios.patch(`/api/contractor/work-orders/${id}/progress`, progressData),

  // Submit work order for approval
  submitForApproval: (id, submissionData) =>
    axios.post(`/api/contractor/work-orders/${id}/submit`, submissionData),

  // Get contractor work order statistics
  getStatistics: () =>
    axios.get("/api/contractor/work-orders/statistics/dashboard"),
};

// Roster Management API (v2.3)
export const rosterManagementAPI = {
  getAll: (params) => axios.get("/api/roster-management", { params }),
  getById: (id) => axios.get(`/api/roster-management/${id}`),

  // Create rosters
  createWeekly: (data) => axios.post("/api/roster-management/weekly", data),
  createMonthly: (data) => axios.post("/api/roster-management/monthly", data),

  // Status management
  updateStatus: (id, statusData) =>
    axios.patch(`/api/roster-management/${id}/status`, statusData),

  // Attendance
  markAttendance: (id, attendanceData) =>
    axios.post(`/api/roster-management/${id}/mark-attendance`, attendanceData),

  // Filter options
  getFilterOptions: () => axios.get("/api/roster-management/meta/filters"),

  // Export
  exportExcel: (id) =>
    axios.get(`/api/roster-management/${id}/export/excel`, {
      responseType: "blob",
    }),
};

// Export all APIs
const api = {
  auth: authAPI,
  dashboard: dashboardAPI,
  workers: workersAPI,
  contractors: contractorsAPI,
  attendance: attendanceAPI,
  compliance: complianceAPI,
  reports: reportsAPI,
  devices: devicesAPI,
  notifications: notificationsAPI,
  settings: settingsAPI,
  superAdmin: superAdminAPI,
  file: fileAPI,
  shifts: shiftsAPI,
  shiftManagement: shiftManagementAPI,
  attendancePolicies: attendancePoliciesAPI,

  // Reports APIs
  clraReports: clraReportsAPI,
  statutoryReports: statutoryReportsAPI,
  analyticsReports: analyticsReportsAPI,

  // v2.3 APIs
  workerDetails: workerDetailsAPI,
  contractorDetails: contractorDetailsAPI,
  workOrderDetails: workOrderDetailsAPI,
  rosterManagement: rosterManagementAPI,
};

export default api;
